// src/modules/access/Login/Login.jsx
import { useEffect, useRef, useState } from 'react';

import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';

import { APP_VERSION } from '@/constants';
import { authLogin, clearImagingStudyFilters } from '@/modules/access/api/auth';
import { getInitialRouteForUser } from '@/modules/access/api/permissions';
import { fetchDashboardVersion } from '@/services/version';
import TopBar from '@/shared/layout/TopBar';
import { Button, Input, Checkbox } from '@shared/ui';

import styles from './Login.module.css';

export default function Login() {
  useEffect(() => {
    document.body.classList.add('login-page-active');
    return () => document.body.classList.remove('login-page-active');
  }, []);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState('password');
  const [termsAccepted, setTermsAccepted] = useState(
    localStorage.getItem('termsAccepted') === 'true'
  );
  const [contractAccepted, setContractAccepted] = useState(
    localStorage.getItem('contractAccepted') === 'true'
  );

  const toastRef = useRef(null);

  const loginMutation = useMutation({
    mutationFn: authLogin,
    onSuccess: async response => {
      if (response.type === 'success') {
        const data = response.data;
        localStorage.setItem('token', data.token);
        localStorage.setItem('role', data.user.role);
        localStorage.setItem('isAdmin', data.user.is_superuser);
        localStorage.setItem('username', data.user.username);
        localStorage.setItem('first_name', data.user.first_name);
        localStorage.setItem('termsAccepted', 'true');
        localStorage.setItem('contractAccepted', 'true');
        localStorage.setItem('fund_type', data.user.fund_type);
        localStorage.setItem('is_superuser', data.user.is_superuser);

        queryClient.clear();

        try {
          queryClient.setQueryData(['auth'], {
            isAdmin: Boolean(data.user.is_superuser),
            role: data.user.role,
            token: data.token,
            username: data.user.username,
          });
        } catch (e) {
          console.log(e);
        }

        const _stored = localStorage.getItem('dashboard_version');
        const runtimeVersion = _stored && _stored !== '[object Object]' ? _stored : APP_VERSION;
        localStorage.setItem('dashboard_version', runtimeVersion);

        clearImagingStudyFilters();

        const route = data.user.is_superuser
          ? '/casos'
          : await getInitialRouteForUser(data.user.role);
        queryClient.invalidateQueries({ queryKey: ['insurance'] });
        navigate(route);
      } else {
        toastRef.current.show({
          severity: 'error',
          summary: 'Error',
          detail: response.message,
        });
      }
    },
    onError: error => {
      console.error('Error fetching login', error);
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error',
      });
    },
  });

  const handleLogin = e => {
    e.preventDefault();
    if (!termsAccepted) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Debes aceptar los términos y condiciones',
      });
      return;
    }
    if (!contractAccepted) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Debes aceptar los términos del contrato',
      });
      return;
    }
    loginMutation.mutate({ username: email, password });
  };

  const { data: dashboardVersionData } = useQuery({
    queryKey: ['dashboard_version_runtime'],
    queryFn: fetchDashboardVersion,
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const dashboardVersion = (() => {
    const stored = localStorage.getItem('dashboard_version');
    if (stored && stored !== '[object Object]') return stored;
    if (dashboardVersionData == null) return APP_VERSION;
    if (typeof dashboardVersionData === 'string') return dashboardVersionData;
    if (typeof dashboardVersionData === 'object') {
      return (
        dashboardVersionData.version ??
        dashboardVersionData.dashboard_version ??
        dashboardVersionData.version_name ??
        APP_VERSION
      );
    }
    return String(dashboardVersionData) || APP_VERSION;
  })();

  const isSubmitting = loginMutation.isPending;

  return (
    <>
      <TopBar />
      <Toast ref={toastRef} position="top-center" />

      <div className={styles.loginContainer}>
        <div className={styles.loginShell}>
          <aside className={styles.loginBrand} aria-hidden="true">
            <div className={styles.loginBrandLogo}>
              <img src="/logo.png" alt="CITAMED" className={styles.loginBrandLogoImg} />
            </div>

            <div className={styles.loginBrandHeadline}>
              <h1 className={styles.loginBrandTitle}>
                <em>Automatiza</em> lo manual <br />
                <em>Moderniza</em> tu servicio
              </h1>
            </div>
          </aside>

          <section className={styles.loginFormSide}>
            <div className={styles.loginMobileLogo}>
              <img src="/logo.png" alt="CITAMED" className={styles.loginMobileLogoImg} />
            </div>

            <div className={styles.loginHeader}>
              <h2 className={styles.loginTitle}>Iniciar Sesión</h2>
            </div>

            <form className={styles.loginForm} onSubmit={handleLogin} noValidate>
              <Input
                id="email"
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="doctor@citamed.com"
                autoComplete="email"
              />

              <Input
                id="password"
                label="Contraseña"
                type={passwordMode}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
                rightElement={
                  <button
                    type="button"
                    className={styles.loginInputGroupAddon}
                    onClick={() => setPasswordMode(m => (m === 'password' ? 'text' : 'password'))}
                    aria-label={
                      passwordMode === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'
                    }
                  >
                    <i className={passwordMode === 'password' ? 'pi pi-eye' : 'pi pi-eye-slash'} />
                  </button>
                }
              />

              <div className={styles.loginChecks}>
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  label={
                    <>
                      Acepto los{' '}
                      <a href="/terminos-y-condiciones" target="_blank" rel="noopener noreferrer">
                        Términos y Condiciones
                      </a>{' '}
                      de esta plataforma
                    </>
                  }
                />

                <Checkbox
                  id="contract"
                  checked={contractAccepted}
                  onChange={e => setContractAccepted(e.target.checked)}
                  label={
                    <>
                      Acepto las{' '}
                      <a href="/politicas-de-privacidad" target="_blank" rel="noopener noreferrer">
                        Políticas de Privacidad
                      </a>{' '}
                      de esta plataforma.
                    </>
                  }
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Ingresar
              </Button>
            </form>
          </section>
        </div>
      </div>
    </>
  );
}
